import React, { useState, useEffect } from "react";
import {
    Typography,
    Grid,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { createTheme } from "@mui/material/styles";
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrders } from "../API/jobOrderAPI";
import { getBillingRecords } from '../API/billingAPI';
import BaseWrapper from "../../components/shared/BaseWrapper";
import { fetchAndFilterJOsBySingleDate } from '../JobOrders/utils/JobOrderFilter';

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));

function ViewProductivityReport() {

    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false); // State to manage loading
    const [searchFilter, setSearchFilter] = useState({
        date: (() => {
            const now = new Date();
            const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
            now.setMinutes(now.getMinutes() + philippineTimezoneOffset);
            return now.toISOString().split("T")[0];  // Return today's date
        })(),
    });
    const [JOSummary, setJOSummary] = useState(null)
    const [technicianJOs, setTechnicianJOs] = useState([])
    const [JOServices, setJOServices] = useState([])
    const [JOServiceReports, setJOServiceReports] = useState([])
    const [JOTypes, setJOTypes] = useState([])
    const [FASTcount, setFASTcount] = useState({})


    useEffect(() => {
        (async () => {
            try {
                const fetchedTechnicians = await getTechnicians();
                setTechnicians(fetchedTechnicians);
                console.log(fetchedTechnicians);
                setLoading(false);
            } catch (error) {
                setError("Failed to fetch technicians");
                setLoading(false);
            }
        })();
    }, []);

    const handleSubmitButtonClick = async () => {
        try {
            setIsLoading(true);
            // Single-date helper already applies the 05:00 → next-day 04:59 window
            const { list: jobOrders } = await fetchAndFilterJOsBySingleDate({
                date: searchFilter.date,
                status: [],                // include all JO statuses
                serviceReportStatus: [],   // include all SR statuses
                billingRecordStatus: [],   // include all billing statuses
                getJobOrdersApi: getJobOrders,
            });

            // Create maps to store technician counts for done and not done statuses
            const technicianDoneMap = {};
            const technicianNotDoneMap = {};

            // Iterate through job orders and count technician appearances based on status
            jobOrders.forEach((job) => {
                if (job.technicians && Array.isArray(job.technicians)) {
                    job.technicians.forEach((tech) => {
                        if (job.status === "Done") {
                            technicianDoneMap[tech.id] = (technicianDoneMap[tech.id] || 0) + 1;
                        } else if (job.status === "Not Done") {
                            technicianNotDoneMap[tech.id] = (technicianNotDoneMap[tech.id] || 0) + 1;
                        }
                    });
                }
            });

            const jobOrderSummary = jobOrders.reduce((summary, job) => {
                // Increment total job orders
                summary.total += 1;

                // Check the status of each job and categorize as done or not done
                if (job.status === "Done") {
                    summary.done += 1;
                } else if (job.status === "Not Done") {
                    summary.not_done += 1;
                }

                // Calculate progress percentage
                summary.progress = summary.total > 0
                    ? ((summary.done / summary.total) * 100).toFixed(2) + '%'
                    : '0%';

                return summary;
            }, {
                total: 0,
                done: 0,
                not_done: 0,
                progress: '0%'
            });


            setJOSummary(jobOrderSummary)

            // Generate the technicianJOs list
            const technicianJOs = technicians.map((tech) => {
                return {
                    id: tech.id,
                    firstname: tech.firstname,
                    lastname: tech.lastname,
                    team: tech.team,
                    team_leader: tech.team_leader,
                    done: technicianDoneMap[tech.id] || 0,
                    not_done: technicianNotDoneMap[tech.id] || 0,
                };
            });

            // Group technicians by their team
            const groupedByTeam = technicianJOs.reduce((acc, tech) => {
                const teamName = Array.isArray(tech.team) ? tech.team[0] : tech.team;
                if (!acc[teamName]) {
                    acc[teamName] = [];
                }
                acc[teamName].push(tech);
                return acc;
            }, {});

            setTechnicianJOs(groupedByTeam);

            const excludeServices = ["basic_safety", "driving_4_wheels", "driving_2_wheels"];

            const summary = {};
            const reportSummary = {};

            // Process job orders
            jobOrders.forEach(order => {
                (order.competencies || []).competencies.forEach(competency => {
                    if (!excludeServices.includes(competency)) {
                        // Initialize summary if not exists
                        if (!summary[competency]) {
                            summary[competency] = { done: 0, not_done: 0 };
                        }
                        if (!reportSummary[competency]) {
                            reportSummary[competency] = { done: 0, not_done: 0 };
                        }

                        // Count based on order.status
                        if (order.status === "Done") {
                            summary[competency].done += 1;
                        } else {
                            summary[competency].not_done += 1;
                        }

                        // Count based on service_report_status
                        if (order.service_report_status === "Done") {
                            reportSummary[competency].done += 1;
                        } else {
                            reportSummary[competency].not_done += 1;
                        }
                    }
                });
            });

            // Helper function to format and sort results
            const formatAndSort = (data) => {
                return Object.keys(data)
                    .map(key => {
                        let formattedKey = key
                            .replace(/_/g, ' ')  // Replace underscores with spaces
                            .replace(/\b\w/g, char => char.toUpperCase());  // Capitalize each word

                        if (formattedKey.toLowerCase() === 'qpms') {
                            formattedKey = 'QPMS';
                        }

                        return {
                            service: formattedKey,
                            done: data[key].done,
                            not_done: data[key].not_done,
                            total: data[key].done + data[key].not_done
                        };
                    })
                    .sort((a, b) => {
                        const order = ['General Pest Control', 'Termite Control', 'Disinfection', 'QPMS'];
                        return order.indexOf(a.service) - order.indexOf(b.service);
                    });
            };

            // Generate outputs
            const JOServices = formatAndSort(summary);
            const JOServiceReports = formatAndSort(reportSummary);


            setJOServices(JOServices)
            setJOServiceReports(JOServiceReports)


            const joSummary = jobOrders.reduce((acc, job) => {
                const jobType = job.job_type || 'unknown';
                const status = job.status.toLowerCase();

                // Check if the job type already exists in the accumulator
                const existingType = acc.find((item) => item.job_type === jobType);

                if (existingType) {
                    // Increment 'done' or 'not_done' count
                    if (status === 'done') {
                        existingType.done += 1;
                    } else if (status === 'not done') {
                        existingType.not_done += 1;
                    }
                } else {
                    // Create a new entry if job type doesn't exist
                    acc.push({
                        job_type: jobType,
                        done: status === 'done' ? 1 : 0,
                        not_done: status === 'not done' ? 1 : 0,
                    });
                }

                return acc;
            }, []);

            setJOTypes(joSummary);

            // Get data for FAST
            const billedRecords = await getBillingRecords(
                '',
                '',
                searchFilter.date || '',
                searchFilter.date || '',
                ''
            );

            const paymentRecords = await getBillingRecords(
                '',
                '',
                '',
                '',
                searchFilter.date
            );

            // Calculate counts for billing and collection
            const billingDoneCount = billedRecords.filter(record => record.payment_status.toLowerCase() === 'billed').length;
            const billingNotDoneCount = billedRecords.filter(record => record.payment_status.toLowerCase() === 'not billed').length;

            const collectionDoneCount = paymentRecords.filter(record => record.payment_status.toLowerCase() === 'paid').length;

            // Calculate totals for certificate from JOServiceReports
            const certificateDoneCount = JOServiceReports.reduce((sum, report) => sum + report.done, 0);
            const certificateNotDoneCount = JOServiceReports.reduce((sum, report) => sum + report.not_done, 0);

            // Set FAST count
            setFASTcount({
                billing: {
                    done: billingDoneCount,
                    not_done: billingNotDoneCount,
                },
                collection: {
                    done: collectionDoneCount,
                    not_done: billingDoneCount,
                },
                certificate: {
                    done: certificateDoneCount,
                    not_done: certificateNotDoneCount,
                },
            });


            setError(null); // Clear any previous errors
        } catch (err) {
            setError(err.message);
            setLoading(false);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <BaseWrapper
            titleIcon={<TrendingUpIcon />}
            title="Operations"
            subTitle="View Productivity Report"
            subTitleRoute={"/view-productivity-report"}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <Grid item xs={12} sm={12}>
                <Grid container spacing={3} sx={{ mb: 2 }} alignItems="center">
                    <Grid item xs={12} sm={2} md={2}>
                        <TextField
                            label="Date"
                            type="date"
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={searchFilter.date}
                            onChange={(e) =>
                                setSearchFilter((prev) => ({
                                    ...prev,
                                    date: e.target.value,
                                }))
                            }
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={2}
                        container
                    // justifyContent="flex-end"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmitButtonClick}
                            disabled={isLoading}
                        >
                            {isLoading ? "Submitting..." : "Submit"}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <StyledHr />
            {Object.keys(technicianJOs).length > 0 && (
                <Card variant="outlined" sx={{ padding: "20px" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Grid container spacing={4} justifyContent="center" alignItems="center">

                                {/* Total Job Orders - Circular Progress with Text Overlay */}
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center', position: 'relative' }}>
                                    <svg width="180" height="180">
                                        <circle cx="90" cy="90" r="70" stroke="#e3f2fd" strokeWidth="15" fill="none" />
                                        <circle
                                            cx="90"
                                            cy="90"
                                            r="70"
                                            stroke="#1976d2"
                                            strokeWidth="15"
                                            fill="none"
                                            strokeDasharray="440"
                                            strokeDashoffset={`calc(440 - (440 * ${JOSummary.total ? 1 : 0}))`}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            fontWeight: 'bold',
                                            color: '#1976d2',
                                        }}
                                    >
                                        {JOSummary.total}
                                    </Typography>
                                    <Typography variant="body1" sx={{ marginTop: '10px', color: '#1976d2' }}>
                                        Total Job Orders
                                    </Typography>
                                </Grid>

                                {/* Completed Jobs - Animated Ring */}
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center', position: 'relative' }}>
                                    <svg width="180" height="180">
                                        <circle cx="90" cy="90" r="70" stroke="#e8f5e9" strokeWidth="15" fill="none" />
                                        <circle
                                            cx="90"
                                            cy="90"
                                            r="70"
                                            stroke="#4caf50"
                                            strokeWidth="15"
                                            fill="none"
                                            strokeDasharray="440"
                                            strokeDashoffset={`calc(440 - (440 * ${JOSummary.done / JOSummary.total || 0}))`}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            fontWeight: 'bold',
                                            color: '#4caf50',
                                        }}
                                    >
                                        {JOSummary.done}
                                    </Typography>
                                    <Typography variant="body1" sx={{ marginTop: '10px', color: '#4caf50' }}>
                                        Jobs Completed
                                    </Typography>
                                </Grid>

                                {/* Pending Jobs - Circular Indicator */}
                                <Grid item xs={12} md={4} sx={{ textAlign: 'center', position: 'relative' }}>
                                    <svg width="180" height="180">
                                        <circle cx="90" cy="90" r="70" stroke="#ffebee" strokeWidth="15" fill="none" />
                                        <circle
                                            cx="90"
                                            cy="90"
                                            r="70"
                                            stroke="#e53935"
                                            strokeWidth="15"
                                            fill="none"
                                            strokeDasharray="440"
                                            strokeDashoffset={`calc(440 - (440 * ${JOSummary.not_done / JOSummary.total || 0}))`}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            fontWeight: 'bold',
                                            color: '#e53935',
                                        }}
                                    >
                                        {JOSummary.not_done}
                                    </Typography>
                                    <Typography variant="body1" sx={{ marginTop: '10px', color: '#e53935' }}>
                                        Pending Jobs
                                    </Typography>
                                </Grid>

                                {/* Progress - Horizontal Bar */}
                                <Grid item xs={12} sx={{ textAlign: 'center', marginTop: '40px' }}>
                                    <Typography variant="h6" sx={{ marginBottom: '10px', fontWeight: 'bold', color: '#1565c0' }}>
                                        JOB ORDER COMPLETION RATE
                                    </Typography>
                                    <div
                                        style={{
                                            width: '70%',
                                            height: '20px',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '10px',
                                            margin: 'auto',
                                            overflow: 'hidden',
                                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${JOSummary.progress}`,
                                                height: '100%',
                                                backgroundColor: '#1e88e5',
                                                transition: 'width 1.5s ease-in-out',
                                            }}
                                        />
                                    </div>
                                    <Typography variant="h4" sx={{ marginTop: '15px', fontWeight: 'bold', color: '#1976d2' }}>
                                        {JOSummary.progress}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '10px', color: '#1976d2' }}>
                                Department Points Summary
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                                            Department
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                                            Done
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                                            Not Done
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                                            Total
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white' }}>
                                            %
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        // Calculate totals for Operations
                                        const totalDoneOps = Object.values(technicianJOs).reduce(
                                            (acc, team) => acc + team.reduce((sum, tech) => sum + tech.done, 0),
                                            0
                                        );
                                        const totalNotDoneOps = Object.values(technicianJOs).reduce(
                                            (acc, team) => acc + team.reduce((sum, tech) => sum + tech.not_done, 0),
                                            0
                                        );
                                        const totalOps = totalDoneOps + totalNotDoneOps;
                                        const completionOps = totalOps ? ((totalDoneOps / totalOps) * 100).toFixed(2) + '%' : '0%';

                                        // Calculate totals for OPAC using JOServices
                                        const totalDoneOpac = JOServices.reduce((sum, s) => sum + s.done, 0);
                                        const totalNotDoneOpac = JOServices.reduce((sum, s) => sum + s.not_done, 0);
                                        const totalOpac = totalDoneOpac + totalNotDoneOpac;
                                        const completionOpac = totalOpac ? ((totalDoneOpac / totalOpac) * 100).toFixed(2) + '%' : '0%';

                                        // Calculate totals for FAST using FASTcount
                                        const totalDoneFAST = Object.values(FASTcount).reduce((sum, area) => sum + area.done, 0);
                                        const totalNotDoneFAST = Object.values(FASTcount).reduce((sum, area) => sum + area.not_done, 0);
                                        const totalFAST = totalDoneFAST + totalNotDoneFAST;
                                        const completionFAST = totalFAST ? ((totalDoneFAST / totalFAST) * 100).toFixed(2) + '%' : '0%';

                                        return (
                                            <>
                                                {/* Operations Row */}
                                                <TableRow>
                                                    <TableCell>Operations</TableCell>
                                                    <TableCell align="center">{totalDoneOps}</TableCell>
                                                    <TableCell align="center">{totalNotDoneOps}</TableCell>
                                                    <TableCell align="center">{totalOps}</TableCell>
                                                    <TableCell align="center">{completionOps}</TableCell>
                                                </TableRow>

                                                {/* OPAC Row */}
                                                <TableRow>
                                                    <TableCell>OPAC</TableCell>
                                                    <TableCell align="center">{totalDoneOpac}</TableCell>
                                                    <TableCell align="center">{totalNotDoneOpac}</TableCell>
                                                    <TableCell align="center">{totalOpac}</TableCell>
                                                    <TableCell align="center">{completionOpac}</TableCell>
                                                </TableRow>

                                                {/* FAST Row */}
                                                <TableRow>
                                                    <TableCell>FAST</TableCell>
                                                    <TableCell align="center">{totalDoneFAST}</TableCell>
                                                    <TableCell align="center">{totalNotDoneFAST}</TableCell>
                                                    <TableCell align="center">{totalFAST}</TableCell>
                                                    <TableCell align="center">{completionFAST}</TableCell>
                                                </TableRow>

                                                {/* Total Row */}
                                                <TableRow sx={{ backgroundColor: '#e8f5e9', fontWeight: 'bold' }}>
                                                    <TableCell>Total</TableCell>
                                                    <TableCell align="center">{totalDoneOps + totalDoneOpac + totalDoneFAST}</TableCell>
                                                    <TableCell align="center">{totalNotDoneOps + totalNotDoneOpac + totalNotDoneFAST}</TableCell>
                                                    <TableCell align="center">{totalOps + totalOpac + totalFAST}</TableCell>
                                                    <TableCell align="center">
                                                        {totalOps + totalOpac + totalFAST
                                                            ? (
                                                                ((totalDoneOps + totalDoneOpac + totalDoneFAST) /
                                                                    (totalOps + totalOpac + totalFAST)) *
                                                                100
                                                            ).toFixed(2) + '%'
                                                            : '0%'}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        );
                                    })()}
                                </TableBody>
                            </Table>
                        </Grid>

                        <Grid item xs={12} md={12} sx={{ marginTop: "20px", marginBottom: "20px" }}>
                            <Typography
                                variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "0.9rem" }}
                            >
                                Operations: Total Points Per Team
                            </Typography>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                padding: "6px",
                                                fontWeight: "bold",
                                                backgroundColor: "#f7f7f7",  // Very light gray
                                            }}
                                        >
                                            Team
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                padding: "6px",
                                                fontWeight: "bold",
                                                backgroundColor: "#f7f7f7",  // Consistent across cells
                                            }}
                                        >
                                            Leader
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                padding: "6px",
                                                fontWeight: "bold",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Done
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                padding: "6px",
                                                fontWeight: "bold",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Not Done
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                padding: "6px",
                                                fontWeight: "bold",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Total
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {Object.keys(technicianJOs)
                                        .sort()
                                        .map((team) => {
                                            const teamLeader = technicianJOs[team].find(
                                                (tech) => tech.team_leader === "yes"
                                            );
                                            const done = technicianJOs[team].reduce(
                                                (sum, tech) => sum + tech.done,
                                                0
                                            );
                                            const notDone = technicianJOs[team].reduce(
                                                (sum, tech) => sum + tech.not_done,
                                                0
                                            );

                                            return (
                                                <TableRow key={team}>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            padding: "6px",
                                                        }}
                                                    >
                                                        {team}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            padding: "6px",
                                                        }}
                                                    >
                                                        {teamLeader
                                                            ? `${teamLeader.firstname} ${teamLeader.lastname}`
                                                            : "N/A"}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            padding: "6px",
                                                        }}
                                                    >
                                                        {done}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            padding: "6px",
                                                        }}
                                                    >
                                                        {notDone}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontSize: "0.75rem",
                                                            padding: "6px",
                                                        }}
                                                    >
                                                        {done + notDone}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                            }}
                                        >
                                            Total
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                            }}
                                        >
                                            {Object.values(technicianJOs).reduce(
                                                (acc, team) =>
                                                    acc + team.reduce((sum, tech) => sum + tech.done, 0),
                                                0
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                            }}
                                        >
                                            {Object.values(technicianJOs).reduce(
                                                (acc, team) =>
                                                    acc +
                                                    team.reduce((sum, tech) => sum + tech.not_done, 0),
                                                0
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                            }}
                                        >
                                            {Object.values(technicianJOs).reduce(
                                                (acc, team) =>
                                                    acc +
                                                    team.reduce(
                                                        (sum, tech) => sum + tech.done + tech.not_done,
                                                        0
                                                    ),
                                                0
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Grid>

                        {Object.keys(technicianJOs)
                            .sort()  // Sort teams alphabetically
                            .map((team) => {
                                const teamLeader = technicianJOs[team].find((tech) => tech.team_leader === "yes");

                                return (
                                    <Grid item xs={6} md={6} key={team}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "0.9rem" }}>
                                            {`Team ${team}`}
                                        </Typography>
                                        {teamLeader && (
                                            <Typography variant="body2" sx={{ marginBottom: "8px", fontSize: "0.8rem" }}>
                                                {`Team Leader: ${teamLeader.firstname} ${teamLeader.lastname}`}
                                            </Typography>
                                        )}

                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            fontSize: "0.8rem",
                                                            padding: "6px",
                                                            fontWeight: "bold",
                                                            backgroundColor: "#f7f7f7",  // Light gray background
                                                        }}
                                                    >
                                                        Technician
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontSize: "0.8rem",
                                                            padding: "6px",
                                                            fontWeight: "bold",
                                                            backgroundColor: "#f7f7f7",
                                                        }}
                                                    >
                                                        Done
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontSize: "0.8rem",
                                                            padding: "6px",
                                                            fontWeight: "bold",
                                                            backgroundColor: "#f7f7f7",
                                                        }}
                                                    >
                                                        Not Done
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {technicianJOs[team].map((tech) => (
                                                    <TableRow key={tech.id}>
                                                        <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                            {`${tech.firstname} ${tech.lastname}`}
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                            {tech.done}
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                            {tech.not_done}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Total Row */}
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                        Total:
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                        {technicianJOs[team].reduce((sum, tech) => sum + tech.done, 0)}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                        {technicianJOs[team].reduce((sum, tech) => sum + tech.not_done, 0)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                );
                            })}

                        {JOServices && JOServices.length > 0 && (
                            <Grid item xs={4}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "0.9rem" }}
                                >
                                    OPAC
                                </Typography>

                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",  // Light gray background
                                                }}
                                            >
                                                Activity
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Not Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {JOServices.map((service, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.service}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.done}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.not_done}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.total}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                Total:
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServices.reduce((sum, s) => sum + s.done, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServices.reduce((sum, s) => sum + s.not_done, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServices.reduce((sum, s) => sum + s.total, 0)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Grid>
                        )}

                        {JOServiceReports && JOServiceReports.length > 0 && (
                            <Grid item xs={4}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "0.9rem" }}
                                >
                                    Service Report Summary
                                </Typography>

                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",  // Light gray background
                                                }}
                                            >
                                                Activity
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Not Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {JOServiceReports.map((service, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.service}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.done}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.not_done}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {service.total}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                Total:
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServiceReports.reduce((sum, s) => sum + s.done, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServiceReports.reduce((sum, s) => sum + s.not_done, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: "bold", padding: "6px" }}>
                                                {JOServiceReports.reduce((sum, s) => sum + s.total, 0)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Grid>
                        )}

                        {JOTypes && JOTypes.length > 0 && (
                            <Grid item xs={4}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    Job Types
                                </Typography>

                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Job Type
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Not Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {JOTypes.map((job, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {job.job_type.charAt(0).toUpperCase() + job.job_type.slice(1)}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {job.done}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {job.not_done}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {job.done + job.not_done}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                Total:
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {JOTypes.reduce((sum, j) => sum + j.done, 0)}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {JOTypes.reduce((sum, j) => sum + j.not_done, 0)}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {JOTypes.reduce((sum, j) => sum + j.done + j.not_done, 0)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Grid>
                        )}


                        {FASTcount && (
                            <Grid item xs={6}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: "bold",
                                        marginBottom: "8px",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    FAST
                                </Typography>

                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Area
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Not Done
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                    backgroundColor: "#f7f7f7",
                                                }}
                                            >
                                                Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(FASTcount).map(([key, value], index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {value.done}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {value.not_done}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ fontSize: "0.75rem", padding: "6px" }}
                                                >
                                                    {value.done + value.not_done}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                Total:
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {Object.values(FASTcount).reduce((sum, area) => sum + area.done, 0)}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {Object.values(FASTcount).reduce(
                                                    (sum, area) => sum + area.not_done,
                                                    0
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    padding: "6px",
                                                }}
                                            >
                                                {Object.values(FASTcount).reduce(
                                                    (sum, area) => sum + area.done + area.not_done,
                                                    0
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>

                            </Grid>
                        )}
                    </Grid>
                </Card>
            )}
        </BaseWrapper>        
    )
}

export default ViewProductivityReport;